import React from 'react';
import { ArrowRight, Loader2, Search } from 'lucide-react';
import { AppDialogFrame } from '../overlays/AppDialogFrame';
import type { AssistantCapability, AssistantTool } from './types';
import type { AssistantCapabilityBrowserActionState } from './assistantCapabilityBrowser';

export interface AssistantCapabilityBrowserModalProps {
  open: boolean;
  query: string;
  totalCount: number;
  onClose: () => void;
  onQueryChange: (value: string) => void;
  sections: Array<{
    label: string;
    capabilities: AssistantCapability[];
  }>;
  toolSections?: Array<{
    label: string;
    tools: AssistantTool[];
  }>;
  promptSections?: Array<{
    label: string;
    prompts: Array<{
      id: string;
      label: string;
      summary: string;
      badge?: string;
    }>;
  }>;
  getPrimaryAction: (capability: AssistantCapability) => AssistantCapabilityBrowserActionState;
  onPrimaryAction: (capability: AssistantCapability) => void;
  onToolAction?: (tool: AssistantTool) => void;
  onPromptAction?: (promptId: string) => void;
}

export const AssistantCapabilityBrowserModal: React.FC<AssistantCapabilityBrowserModalProps> = ({
  open,
  query,
  totalCount,
  onClose,
  onQueryChange,
  sections,
  toolSections = [],
  promptSections = [],
  getPrimaryAction,
  onPrimaryAction,
  onToolAction,
  onPromptAction,
}) => (
  <AppDialogFrame
    open={open}
    onClose={onClose}
    title="Browser"
    description="Capabilities, prompts, and user-facing tools available to this account."
    testId="ai-capability-catalog"
    insetMode="viewport"
    maxWidthClassName="max-w-none"
    panelClassName="min-h-[min(84dvh,960px)] w-[min(92vw,1120px)]"
    bodyPaddingClassName="px-0 py-0"
    bodyClassName="flex min-h-0 flex-1 flex-col overflow-hidden"
  >
    <div className="border-b border-slate-100 px-5 py-3">
      <div className="mb-2 flex items-center justify-between gap-3">
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-primary/70">Assistant Browser</p>
        <span className="text-[11px] text-irongrey/60">{totalCount} items</span>
      </div>
      <div className="relative">
        <Search
          size={16}
          aria-hidden="true"
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-irongrey/45"
        />
        <input
          type="search"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder="Search capabilities, prompts, or modules..."
          className="h-11 w-full rounded-2xl border border-slate-200 bg-slate-50 pl-10 pr-4 text-sm leading-[44px] text-slate-900 outline-none transition-colors placeholder:text-irongrey/45 focus:border-primary/30 focus:bg-white"
          data-testid="ai-capability-catalog-search"
        />
      </div>
    </div>
    <div className="flex-1 overflow-y-auto px-5 py-4">
      <div className="space-y-5">
        {promptSections.length > 0 ? promptSections.map((section) => (
          <section key={`prompt-${section.label}`} data-testid="ai-capability-catalog-prompt-section">
            <div className="mb-2 flex items-center justify-between">
              <h4 className="text-xs font-semibold uppercase tracking-[0.16em] text-irongrey/60">{section.label}</h4>
              <span className="text-[11px] text-irongrey/50">{section.prompts.length}</span>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {section.prompts.map((prompt) => (
                <div
                  key={prompt.id}
                  className="rounded-[20px] border border-slate-200 bg-white px-4 py-3"
                  data-testid="ai-capability-catalog-prompt-item"
                  data-ai-settings-case-id={prompt.id}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900">{prompt.label}</p>
                      <p className="mt-1 text-xs leading-5 text-irongrey/70">{prompt.summary}</p>
                    </div>
                    {prompt.badge ? (
                      <span className="rounded-full border border-primary/15 bg-primary/5 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-primary/70">
                        {prompt.badge}
                      </span>
                    ) : null}
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-3">
                    <p className="min-w-0 text-[11px] leading-5 text-irongrey/65">
                      Route-aware settings specialist prompt.
                    </p>
                    <button
                      type="button"
                      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-[11px] font-semibold text-white hover:opacity-90"
                      onClick={() => onPromptAction?.(prompt.id)}
                      data-testid={`ai-capability-catalog-prompt-action-${prompt.id}`}
                    >
                      <span>Ask</span>
                      <ArrowRight size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )) : null}
        {toolSections.length > 0 ? (
          toolSections.map((section) => (
            <section key={`tool-${section.label}`} data-testid="ai-capability-catalog-tool-section">
              <div className="mb-2 flex items-center justify-between">
                <h4 className="text-xs font-semibold uppercase tracking-[0.16em] text-irongrey/60">{section.label} tools</h4>
                <span className="text-[11px] text-irongrey/50">{section.tools.length}</span>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                {section.tools.map((tool) => (
                  <div
                    key={tool.toolId}
                    className="rounded-[20px] border border-slate-200 bg-white px-4 py-3"
                    data-testid="ai-capability-catalog-tool-item"
                    data-ai-tool-id={tool.toolId}
                    data-ai-tool-module={tool.module}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">{tool.title}</p>
                        <p className="mt-1 text-xs leading-5 text-irongrey/70">{tool.summary}</p>
                      </div>
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-irongrey/60">
                        {tool.taskClass ?? tool.executionPlane}
                      </span>
                    </div>
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <p className="min-w-0 text-[11px] leading-5 text-irongrey/65">
                        {tool.targetRoute ? 'Runs with the linked route context.' : 'Runs with the current assistant context.'}
                      </p>
                      <button
                        type="button"
                        className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-[11px] font-semibold text-white hover:opacity-90"
                        onClick={() => onToolAction?.(tool)}
                        data-testid={`ai-capability-catalog-tool-action-${tool.toolId}`}
                      >
                        <span>Use</span>
                        <ArrowRight size={12} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))
        ) : null}
        {sections.length > 0 ? (
          sections.map((section) => (
            <section key={section.label} data-testid="ai-capability-catalog-section">
              <div className="mb-2 flex items-center justify-between">
                <h4 className="text-xs font-semibold uppercase tracking-[0.16em] text-irongrey/60">{section.label}</h4>
                <span className="text-[11px] text-irongrey/50">{section.capabilities.length}</span>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                {section.capabilities.map((capability) => {
                  const primaryAction = getPrimaryAction(capability);
                  const isChecking = primaryAction.kind === 'checking';
                  return (
                    <div
                      key={capability.id}
                      className="rounded-[20px] border border-slate-200 bg-white px-4 py-3"
                      data-testid="ai-capability-catalog-item"
                      data-ai-capability-id={capability.id}
                      data-ai-capability-module={capability.module}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-900">{capability.title}</p>
                          <p className="mt-1 text-xs leading-5 text-irongrey/70">{capability.prompt || capability.rationale}</p>
                        </div>
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-irongrey/60">
                          {capability.type}
                        </span>
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-3">
                        <p className="min-w-0 text-[11px] leading-5 text-irongrey/65">
                          {primaryAction.helperText}
                        </p>
                        <button
                          type="button"
                          className={`inline-flex shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-[11px] font-semibold transition-colors ${
                            primaryAction.disabled
                              ? 'cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400'
                              : primaryAction.kind === 'open_page'
                                ? 'border border-primary/20 bg-primary/5 text-primary hover:bg-primary/10'
                                : 'bg-primary text-white hover:opacity-90'
                          }`}
                          onClick={() => onPrimaryAction(capability)}
                          disabled={primaryAction.disabled}
                          data-testid={`ai-capability-catalog-primary-action-${capability.id}`}
                        >
                          {isChecking ? <Loader2 size={12} className="animate-spin" /> : null}
                          <span>{primaryAction.label}</span>
                          {!primaryAction.disabled && !isChecking ? <ArrowRight size={12} /> : null}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))
        ) : promptSections.length === 0 && toolSections.length === 0 ? (
          <div
            className="rounded-[20px] border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-irongrey/60"
            data-testid="ai-capability-catalog-empty"
          >
            No assistant items matched this search.
          </div>
        ) : null}
      </div>
    </div>
  </AppDialogFrame>
);
